import React, { useEffect, useState } from "react";
import { useCookies } from "react-cookie";

export default function SelectPageBinance() {
  const [cookies] = useCookies(["uuid"]);
  const [uid, setUID] = useState(0);
  const [nickname, setNickname] = useState("");
  const [processingKey, setProcessingKey] = useState(null);

  useEffect(() => {
    if (!cookies.uuid) {
      alert("로그인 정보가 존재하지 않습니다. 로그인 페이지로 이동합니다.");
      window.location.href = `/LoginAtsBinance`;
      return;
    }
    fetchUserInfo(cookies.uuid);
  }, [cookies.uuid]);

  const fetchUserInfo = async (uuid) => {
    try {
      const response = await fetch(`/SetPage/user_binance?uuid=${uuid}`);
      if (!response.ok) {
        throw new Error("서버 응답 실패");
      }
      const data = await response.json();
      const user = data[0];
      if (!user) {
        throw new Error("사용자 없음");
      }
      setUID(user.uid);
      setNickname(user.nickname);
    } catch (error) {
      alert("로그인 정보가 존재하지 않습니다. 로그인 페이지로 이동합니다.");
      window.location.href = `/LoginAtsBinance`;
    }
  };

  const handleVersionSelect = async (option) => {
    if (!uid || option.disabled || processingKey) {
      return;
    }
    setProcessingKey(option.key);
    try {
      const response = await fetch(`${option.endpoint}?uid=${uid}`);
      if (!response.ok) {
        throw new Error("네트워크 오류");
      }
      const result = await response.json();
      if (result?.message && result.message !== "ok") {
        alert(result.message);
        return;
      }
      if (option.successMessage) {
        alert(option.successMessage);
      }
      window.location.href = option.redirect;
    } catch (error) {
      console.error(error);
      alert("서비스 연결에 실패했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setProcessingKey(null);
    }
  };

  const liveStrategies = [
    {
      key: "wz-binance",
      label: "Wizard",
      subtitle: "Delta hedge · Swing",
      description: "멀티 심볼 진입/대기 리스트를 Binance 환경에 맞춰 자동 관리합니다.",
      badge: "LIVE",
      icon: "🧙‍♂️",
      endpoint: "/wzversion_selected_binance",
      redirect: "/SetpageWzBinance",
      successMessage: "Move to WZ version page",
      accent: "#34d399",
    },
    {
      key: "hedge-binance",
      label: "Hedge",
      subtitle: "Risk off · Multi leg",
      description: "급등락 시에도 포트폴리오 전반을 감싸는 안정형 헷지 버전입니다.",
      badge: "LIVE",
      icon: "🛡️",
      endpoint: "/hedgeversion_selected_binance",
      redirect: "/SetPageHedgeBinance",
      successMessage: "Move to Hedge version page",
      accent: "#0ea5e9",
    },
  ];

  const comingSoonStrategies = [
    {
      key: "circle-binance",
      label: "Circle",
      subtitle: "Loop rebalance",
      description: "루프형 분산 전략이 Binance에서도 곧 제공됩니다.",
      badge: "SOON",
      icon: "⭕",
      endpoint: "/circleversion_selected_binance",
      redirect: "/SetPageCircle",
      successMessage: "Move to Circle version page",
      accent: "#22d3ee",
      disabled: true,
    },
    {
      key: "auto-binance",
      label: "Auto Grid",
      subtitle: "Smart grid",
      description: "자동 포지션 조정 툴을 Binance에 최적화하는 중입니다.",
      badge: "SOON",
      icon: "⚙️",
      endpoint: "/autogrid_selected_binance",
      redirect: "/SetPageCirclePro",
      successMessage: "Move to Auto Grid page",
      accent: "#5eead4",
      disabled: true,
    },
  ];

  const isUserReady = Boolean(uid);

  const renderStrategyButton = (option) => {
    const isBusy = processingKey === option.key;
    const isDisabled = option.disabled || !isUserReady || isBusy;
    return (
      <button
        key={option.key}
        type="button"
        className={`select-card${isDisabled ? " select-card--disabled" : ""}${
          isBusy ? " select-card--loading" : ""
        }`}
        style={{ "--card-accent": option.accent }}
        onClick={() => handleVersionSelect(option)}
        disabled={isDisabled}
      >
        <div className="select-card__top">
          <span className="select-card__badge">{option.badge}</span>
          <span className="select-card__icon" aria-hidden="true">
            {option.icon}
          </span>
        </div>
        <div className="select-card__body">
          <div>
            <p className="select-card__title">{option.label}</p>
            <p className="select-card__subtitle">{option.subtitle}</p>
          </div>
          <p className="select-card__description">{option.description}</p>
        </div>
        <div className="select-card__footer">
          {isDisabled && option.disabled ? (
            <span className="select-card__status">Coming soon</span>
          ) : (
            <span className="select-card__cta">
              {isBusy ? "연결 중..." : "지금 접속"}
            </span>
          )}
        </div>
      </button>
    );
  };

  return (
    <main className="select-page select-page--binance">
      <div className="select-page__glare" aria-hidden="true" />
      <section className="select-page__panel">
        <header className="select-page__header">
          <p className="select-page__eyebrow">Bitsoop Binance suite</p>
          <h1 className="select-page__title">Binance automation cockpit</h1>
          <p className="select-page__subtitle">
            변동성이 큰 알트마켓에서도 원하는 비중과 리스크를 유지하도록 설계되었습니다.
          </p>
          <p className="select-page__subtitle">
            Binance 전용 전략 세트를 선택하세요.
          </p>
          <div className="select-page__user-card">
            <span className="select-page__user-id">
              UID {isUserReady ? uid : "Loading..."}
            </span>
            <span className="select-page__user-name">
              {nickname ? `${nickname}님 환영합니다` : "사용자 정보를 불러오는 중"}
            </span>
          </div>
        </header>

        <section className="select-section">
          <div className="select-section__head">
            <div>
              <p className="select-section__eyebrow">LIVE STRATEGIES</p>
              <h2 className="select-section__title">Binance에서 즉시 실행</h2>
            </div>
            <p className="select-section__meta">Binance · 안정 검증 완료</p>
          </div>
          <div className="select-grid">{liveStrategies.map(renderStrategyButton)}</div>
        </section>

        <section className="select-section">
          <div className="select-section__head">
            <div>
              <p className="select-section__eyebrow">LAB UPDATE</p>
              <h2 className="select-section__title">곧 추가될 Binance 버전</h2>
            </div>
            <p className="select-section__meta">사전 예약 및 커스텀 준비 중</p>
          </div>
          <div className="select-grid">
            {comingSoonStrategies.map(renderStrategyButton)}
          </div>
        </section>
      </section>
    </main>
  );
}